import React from 'react';
import { getBoxShadowStyle } from '@styles/shadow';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { Element, scale as initialScale, notchBottom } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import Loading from '@components/loading';
import ModalWrapper from '@components/modal/ModalWrapper';
import { StyleProp, StyleSheet, Text, TextStyle, View, ViewStyle } from 'react-native';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import { isPhone, isWeb } from '@helpers';
import { IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT } from '@v2/pages/common/constants/base.constants';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { ColorFunction } from '@styles/colors/types';
import { SimpleOnPress } from '@interfaces/base';
import i18n from 'i18n-js';

export interface ContextMenuModalItemIcon {
  name: string;
  width?: number;
  height?: number;
  color?: string;
  transform?: string;
}

export interface ContextMenuModalItem extends SimpleOnPress {
  title: string;
  icon: ContextMenuModalItemIcon | ContextMenuModalItemIcon[];
  iconGap?: number;
  color?: string;
  disabled?: boolean;
  width?: number;
  height?: number;
  style?: StyleProp<ViewStyle>;
}

export interface ContextMenuModalProps {
  scale?: ScaleFunction;
  C: ColorFunction;
  loading?: boolean;
  font?: typeof initialFont;
  onClose: () => void;
  list: ContextMenuModalItem[];
  isVisible?: boolean;
  globalItemStyle?: StyleProp<ViewStyle>;
  textItemStyle?: StyleProp<TextStyle>;
}

const ContextMenuModal = ({
  C,
  list,
  loading,
  onClose,
  isVisible,
  globalItemStyle,
  textItemStyle,
  scale = initialScale,
  font = initialFont,
}: ContextMenuModalProps) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Ultra Wide', C, scheme: 'light' });

  const { safeIsOperatorPanelInfoScreen } = useNavigationCheck();

  const borderRadius = scale(10);

  if (loading) {
    return (
      <Element
        scale={scale}
        {...{
          config: {
            position: {
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            },
            zIndex: 100000,
            backgroundColor: 'black',
            opacity: 0.6,
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          },
        }}
      >
        <Loading background="rgb(0,0,0)" />
      </Element>
    );
  }

  const LINE_HEIGHT = scale(48);

  const safeAreaPosition: StyleProp<ViewStyle> = isPhone
    ? { bottom: IMAGE_HANDLING_MOBILE_FOOTER_HEIGHT }
    : { top: scale(safeIsOperatorPanelInfoScreen && !isWeb && !isPhone ? 80 : 70) };

  const disableColor = 'Button/Light/Secondary';
  const defaultColor = 'Label/Light/Primary';

  const iconRender = (icon: ContextMenuModalItemIcon | ContextMenuModalItemIcon[], disabled?: boolean) => {
    if (Array.isArray(icon)) {
      return icon.map(({ name, color = defaultColor, height = 20, width = 20, ...iconProps }) => (
        <Svg key={name} icon={name} width={scale(height)} height={scale(width)} color={disabled ? disableColor : color} {...iconProps} />
      ));
    }

    const { name, color = defaultColor, height = 20, width = 20 } = icon;

    return <Svg icon={name} width={scale(width)} height={scale(height)} color={disabled ? disableColor : color} />;
  };

  return (
    <ModalWrapper isVisible={isVisible} onBackdropPress={onClose} C={C} backdropColor="transparent" backdropOpacity={1}>
      <View
        style={[
          {
            position: 'absolute',
            right: scale(!isPhone ? 20 : 8),
            minWidth: scale(232),
            backgroundColor: C('Background/Light/Field on modal'),
            borderRadius,
            elevation: 6,
            zIndex: 999999,
            ...shadowStyles,
          },
          safeAreaPosition,
        ]}
      >
        {list.map((item, index) => (
          <TouchableElement
            key={item.title}
            onPress={() => {
              if (item.disabled) {
                return;
              }

              item.onPress();
              onClose();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View
                style={[
                  boxStyle,
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingLeft: scale(15),
                    paddingRight: scale(22),
                    height: LINE_HEIGHT,
                    opacity: item.disabled ? 0.5 : 1,
                    borderTopLeftRadius: index === 0 ? borderRadius : 0,
                    borderTopRightRadius: index === 0 ? borderRadius : 0,
                    borderBottomLeftRadius: index === list.length - 1 ? borderRadius : 0,
                    borderBottomRightRadius: index === list.length - 1 ? borderRadius : 0,
                    backgroundColor: C(pressHoverColor('Background/Light/Field on modal')),
                    borderBottomColor: index !== list.length - 1 ? C('Hairline/Light/Secondary') : 'transparent',
                    borderBottomWidth: StyleSheet.hairlineWidth,
                  },
                  item.style,
                  globalItemStyle,
                ]}
              >
                <Text
                  style={[
                    font['Body'],
                    { color: C(item.disabled ? disableColor : item.color ?? defaultColor) },
                    { fontWeight: '400', fontSize: scale(16) },
                    textItemStyle,
                  ]}
                >
                  {i18n.t(item.title)}
                </Text>
                <View style={{ gap: item.iconGap || 6, flexDirection: 'row', alignItems: 'center' }}>{iconRender(item.icon)}</View>
              </View>
            )}
          </TouchableElement>
        ))}
      </View>
    </ModalWrapper>
  );
};

export default ContextMenuModal;
